import { Text } from "@blockdaemon-internal/design-system";
import { Trans } from "@lingui/react/macro";
import {
  Accordion,
  Alert,
  Box,
  Button,
  Code,
  Container,
  Divider,
  Flex,
  Group,
  Space,
  Stack,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useState } from "react";
import badgePurple from "../../assets/blockdeamon-badge-purple.svg";
import badgeWhite from "../../assets/blockdeamon-badge-white.svg";
import { getPageBackgroundColor } from "../utils/theme-colors";

interface AppError extends Error {
  code: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

interface ErrorFallbackProps {
  error: unknown;
  resetError?: () => void;
  eventId?: string;
  source?: "global" | "router" | "widget";
}

export function ErrorFallback({
  error,
  resetError,
  eventId,
  source = "global",
}: ErrorFallbackProps) {
  const [showDetails, setShowDetails] = useState(false);

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const errorMessage = error instanceof Error ? error.message : String(error);
  const isAppError = error instanceof Error && "code" in error;
  const appError = isAppError ? (error as AppError) : null;

  // For global errors, cover the entire viewport to hide Shell header
  // For widget errors, cover the widget container to hide widget header
  const isGlobalError = source === "global";
  const isWidgetError = source === "widget";

  const { colorScheme } = useMantineColorScheme();

  return (
    <Flex
      align="center"
      justify="center"
      w="100%"
      style={
        isGlobalError
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: "100vh",
              minHeight: "100vh",
              zIndex: 9999,
              backgroundColor: getPageBackgroundColor(),
            }
          : isWidgetError
            ? {
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                height: "100%",
                minHeight: "100%",
                zIndex: 10,
                backgroundColor:
                  "light-dark(var(--mantine-color-gray-0), var(--mantine-color-gray-dark-11))",
              }
            : {
                minHeight: "100%",
              }
      }
    >
      <Container size="md" py="xl">
        <Stack align="center" gap={0}>
          {/* Header Section - Logo, Title, Description */}
          <Stack align="center" gap="md" mb="2rem">
            <Box>
              {colorScheme === "dark" ? (
                <Box component="img" src={badgeWhite} alt="Blockdaemon Badge White" w={60} h={60} />
              ) : (
                <Box
                  component="img"
                  src={badgePurple}
                  alt="Blockdaemon Badge Purple"
                  w={60}
                  h={60}
                />
              )}
            </Box>

            <Stack align="center" gap="xs" maw={480}>
              <Title order={1} ta="center" size="2rem" fw={600}>
                <Trans>Something went wrong</Trans>
              </Title>

              <Text
                ta="center"
                c="gray.9"
                variant="body"
                style={{
                  lineHeight: 1.5,
                }}
              >
                <Trans>
                  We encountered an unexpected error.
                  <br />
                  Our team has been automatically notified.
                </Trans>
              </Text>
            </Stack>
          </Stack>

          {/* Error Details Section */}
          {appError && (
            <Container size="sm" mb="2rem">
              <Alert color="red" title={<Trans>Error Details</Trans>} w="100%">
                <Stack gap="xs">
                  <Text size="sm">
                    <strong>
                      <Trans>Code:</Trans>
                    </strong>{" "}
                    {appError.code}
                  </Text>
                  <Text size="sm">{appError.message}</Text>
                </Stack>
              </Alert>
            </Container>
          )}

          {/* Actions Section */}
          <Stack align="center" gap="xl" w="100%" maw={400}>
            {/* Primary Actions */}
            <Group gap="lg" justify="center" w="100%">
              <Button onClick={handleRefresh} size="md" style={{ minWidth: "120px" }}>
                <Trans>Try Again</Trans>
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
                size="md"
                style={{ minWidth: "120px" }}
              >
                <Trans>Reload</Trans>
              </Button>
            </Group>

            {/* Divider */}
            <Divider
              w="60%"
              style={{
                borderColor: "light-dark(var(--mantine-color-gray-3), var(--mantine-color-gray-6))",
              }}
            />

            {/* Secondary Actions */}
            <Group gap="md">
              <Button size="sm" onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? <Trans>Hide</Trans> : <Trans>Show</Trans>}{" "}
                <Trans>Technical Details</Trans>
              </Button>
            </Group>
          </Stack>

          {/* Technical Details Section */}
          {showDetails && (
            <>
              <Space h="xl" />
              <Container size="sm" w="100%">
                {eventId ? (
                  /* Sentry Error Display */
                  <Stack gap="md" align="center">
                    <Text size="sm" fw={500} ta="center">
                      <Trans>Technical Information</Trans>
                    </Text>
                    <Code
                      block
                      c="red"
                      fz="xs"
                      p="lg"
                      w="100%"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: "200px",
                        overflow: "auto",
                      }}
                    >
                      {errorMessage}
                    </Code>
                    <Text size="xs" ta="center" fw={500}>
                      <Trans>Error ID:</Trans>{" "}
                      <Box component="span" ff="monospace">
                        {eventId}
                      </Box>
                    </Text>
                  </Stack>
                ) : (
                  /* Router Error Display */
                  <Accordion variant="contained" w="100%">
                    <Accordion.Item value="error-details">
                      <Accordion.Control>
                        <Text size="sm">
                          <Trans>Technical Details</Trans>
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Code
                          block
                          c="red"
                          fz="xs"
                          p="md"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: "200px",
                            overflow: "auto",
                          }}
                        >
                          {errorMessage}
                        </Code>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )}
              </Container>
            </>
          )}

          {/* Footer Section */}
          <Space h="2rem" />
          <Container size="sm">
            <Text
              variant="body"
              c="gray.9"
              ta="center"
              size="sm"
              style={{
                lineHeight: 1.4,
              }}
            >
              <Trans>
                If this problem persists, please contact support
                <br />
                and reference the error details above.
              </Trans>
            </Text>
          </Container>
        </Stack>
      </Container>
    </Flex>
  );
}
